import React, { useContext, useMemo } from "react";
import PropTypes from "prop-types";
import { Text } from "./base/index";
import Article from "./Article";
import Step from "./Step";
import GlossaryTerms from "./GlossaryTerms"; 
import HeadingWrapper from "./HeadingWrapper";
import useGlossaryHandler from "../hooks/useGlossaryHandler";
import { PageStateContext } from "../PageContext";
import useConditionEvaluator from "../hooks/useConditionEvaluator";
import { debugLog, renderHtmlString } from "../utils/index";
import {
  createStatusInfo,
  combineStatusInfo,
  createConditionStatusInfo,
} from "../utils/contentStatusUtils";
import { contentProps } from "./propTypes";
import dciLogo from "../images/DCI.png";

// Permalink icon: DCI logo linking to the universal /link/ page
const DigiGuideLinkIcon = ({ href }) => (
  <a
    href={href}
    title="Permalink"
    style={{ marginLeft: "0.3em", display: "inline-flex", alignItems: "center", justifyContent: "center", width: "16px", height: "16px", background: "#0B83FF", borderRadius: "3px", verticalAlign: "middle", textDecoration: "none", flexShrink: 0 }}
  >
    <img src={dciLogo} alt="Permalink" style={{ width: "10px", height: "10px", objectFit: "contain" }} />
  </a>
);

const Topic = ({
  entry,
  isSolo = false,
  changedType = 0,
  changedData = null,
  chapterIdentifier = "unknown",
  topicIndex = 0,
}) => {
  if (!entry) {
    return <div>No content available.</div>;
  }

  const { title, htmlTitle, content, steps, id } = entry;

  const { pageState } = useContext(PageStateContext);
  const { isWebView, manualId: pageManualId } = pageState;
  const { evaluateConditions } = useConditionEvaluator();

  if (!pageState || !pageState.uiText || pageState.uiText.length < 1) {
    return (
      <div className="header-loading">
        <span>Loading Topic...</span>
      </div>
    );
  }

  const { chrome } = pageState;
  const { pageLayoutType } = chrome;
  const isQSG = pageLayoutType === "QSG";
  debugLog("Topic pageLayoutType", pageLayoutType);

  const textTitleColorClass = isQSG ? "text-blue-accent" : "text-grey-2";
  debugLog("topic content", content);
  debugLog("topic steps", steps);
  const { handleGlossaryAnchor, extractTermIdFromHref, isValidTermId } = useGlossaryHandler();

  // Collect glossary IDs synchronously so they're present on the FIRST render
  // that PagedJS captures. The useEffect-based approach in RichText fires too late
  // in static PDF mode — PagedJS already processes the DOM before the state update.
  const glossaryIds = useMemo(() => {
    const ids = new Set();
    const fromHtml = html => {
      if (typeof html !== "string") return;
      const re = /href="([^"]+)"/g;
      let m;
      // eslint-disable-next-line no-cond-assign
      while ((m = re.exec(html)) !== null) {
        const id = extractTermIdFromHref(m[1]);
        if (id && isValidTermId(id)) ids.add(Number(id));
      }
    };
    const walkBlocks = blocks => {
      if (!Array.isArray(blocks)) return;
      blocks.forEach(b => {
        if (!b) return;
        if (b.content?.paragraph) fromHtml(b.content.paragraph);
        if (Array.isArray(b.content?.columnBlockComposableEntries))
          walkBlocks(b.content.columnBlockComposableEntries);
        if (Array.isArray(b.steps)) b.steps.forEach(s => walkBlocks(s?.content));
      });
    };
    walkBlocks(content);
    if (Array.isArray(steps)) steps.forEach(s => walkBlocks(s?.content));
    return Array.from(ids);
  }, [content, steps, extractTermIdFromHref, isValidTermId]);

  // Create a unique identifier for this topic that combines chapter and topic info
  const topicIdentifier = id || `${chapterIdentifier}-topic-${topicIndex}`;

  // Evaluate conditions for the entire topic
  const {
    shouldHide,
    conditionSummary,
    hasConditions,
    activeRestrictiveConditions,
  } = evaluateConditions(entry);

  // Determine if restrictive conditions exist to set changedType
  const conditionsExist = hasConditions;

  // Create status info objects
  const conditionStatusInfo = createConditionStatusInfo(
    shouldHide,
    conditionSummary
  );
  const changeStatusInfo = createStatusInfo(
    changedType,
    changedData ? `Changed: ${Object.keys(changedData).join(", ")}` : ""
  );

  // Get combined status info that includes both condition and change information
  const statusInfo = combineStatusInfo(conditionStatusInfo, changeStatusInfo);

  // Determine wrapper classes
  const wrapperClassNames = [
    pageState.isPreview && (conditionsExist || changeStatusInfo.status)
      ? "edited-error tooltip"
      : "",
    shouldHide && pageState.isPreview ? "semi-transparent-red-bg" : "",
  ]
    .filter(Boolean)
    .join(" ");

  debugLog("Topic pageState for isPreview", pageState.isPreview)
  // Early return if topic should not be rendered and not in preview
  if (shouldHide && !pageState.isPreview) {
    debugLog(
      `Hiding Topic - ID: ${entry.id || "N/A"}, Title: ${
        entry.title || "N/A"
      }, Conditions: ${conditionSummary || "None"}, hasConditions: ${hasConditions}, activeRestrictiveConditions: ${activeRestrictiveConditions}`
    );
    return null;
  }

  // Check if we have steps to render
  const hasSteps = steps && steps.length > 0;

  // Check if we have a valid title (not empty or just spaces)
  const hasTitle = title && title.trim() !== "" && title !== " " && !isSolo;

  const renderedHtmlTitle = htmlTitle ? renderHtmlString(htmlTitle) : title;
  // Title classes based on conditions
  const titleClasses = statusInfo.status
    ? wrapperClassNames
    : `${textTitleColorClass} title-topic mb-3 mt-10 ${
        !hasSteps ? "break-after-avoid" : ""
      }`;

  return (
    <div className={!isSolo ? "mt-10 topic" : "mt-0 topic"}>
      {/* When we have steps, we need to ensure the first step stays with the title (if there is one) */}
      {hasSteps ? (
        <>
          {/* If we have a title, wrap it and the first step to keep them together */}
          {hasTitle ? (
            <div className="break-inside-avoid">
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: '0.3em' }}>
                <HeadingWrapper
                  text={title}
                  type="topic"
                  identifier={topicIdentifier}
                  style={{ flex: '1 1 auto', minWidth: 0 }}
                >
                  <Text
                    tag="h3"
                    use="label"
                    options={{size: "text-body" }}
                    className={titleClasses}
                  >
                    {statusInfo.status && changedData && changedData.entryName
                      ? changedData.entryName
                      : renderedHtmlTitle}
                    {statusInfo.status ? (
                      <span className="tooltiptext">{statusInfo.msg}</span>
                    ) : null}
                  </Text>
                </HeadingWrapper>
                {isWebView && id && pageManualId && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', flexShrink: 0, marginBottom: 'calc(0.75rem + 0.2em)' }}>
                    <DigiGuideLinkIcon href={`/link/TopicArticle?id=${id}&chapterId=${chapterIdentifier}&manualId=${pageManualId}`} />
                  </span>
                )}
              </div>

              {/* First step - rendered within the break-inside-avoid container */}
              <Step
                key={steps[0].id}
                id={steps[0].id}
                number={1}
                total={steps.length}
                handleAnchor={handleGlossaryAnchor}
                isNested={false}
                {...steps[0]}
              />
            </div>
          ) : (
            /* No title, but we still render the first step */
            <Step
              key={steps[0].id}
              id={steps[0].id}
              number={1}
              total={steps.length}
              handleAnchor={handleGlossaryAnchor}
              isNested={false}
              {...steps[0]}
            />
          )}

          {/* Remaining steps - start from index 1 (second step) */}
          {steps.slice(1).map((item, idx) => (
            <Step
              key={item.id}
              id={item.id}
              number={idx + 2} // +2 because we're starting from the second step (index 1)
              total={steps.length}
              handleAnchor={handleGlossaryAnchor}
              isNested={false}
              {...item}
            />
          ))}
        </>
      ) : (
        /* If no steps, handle content-only topics */
        <>
          {/* Render title if it exists */}
          {hasTitle && (
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: '0.3em' }}>
              <HeadingWrapper
                text={renderedHtmlTitle}
                type="topic"
                identifier={topicIdentifier}
                style={{ flex: '1 1 auto', minWidth: 0 }}
              >
                <Text
                  tag="h3"
                  use="label"
                  options={{ size: "text-body" }}
                  className={titleClasses}
                >
                  {statusInfo.status && changedData && changedData.entryName
                    ? renderHtmlString(changedData.entryName)
                    : renderedHtmlTitle}
                  {statusInfo.status ? (
                    <span className="tooltiptext">{statusInfo.msg}</span>
                  ) : null}
                </Text>
              </HeadingWrapper>
              {isWebView && id && pageManualId && (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.15em', flexShrink: 0, paddingBottom: '0.25em' }}>
                  <DigiGuideLinkIcon href={`/link/TopicArticle?id=${id}&chapterId=${chapterIdentifier}&manualId=${pageManualId}`} />
                </span>
              )}
            </div>
          )}

          {/* Article content */}
          {content && (
            <Article content={content} handleAnchor={handleGlossaryAnchor} />
          )}
        </>
      )}

      <GlossaryTerms glossaryIds={glossaryIds} />
    </div>
  );
};

Topic.propTypes = {
  entry: PropTypes.shape({
    id: PropTypes.number.isRequired,
    title: PropTypes.string.isRequired,
    showForThisSiteLanguage: PropTypes.bool,
    showForTheFollowingProducts: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        title: PropTypes.string.isRequired,
        typeHandle: PropTypes.string,
      })
    ),
    showForTheFollowingProductCategories: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
      })
    ),
    content: contentProps,
    steps: PropTypes.arrayOf(PropTypes.object),
  }).isRequired,
  isSolo: PropTypes.bool,
  changedType: PropTypes.number,
  changedData: PropTypes.object,
  chapterIdentifier: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  topicIndex: PropTypes.number,
};

export default Topic;